import { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import api from '@utils/api';
import { trackWishlistAdd, trackWishlistRemove } from '@utils/analytics';
import { useAuth } from '@context/AuthContext';
import {
  getWishlistActivityEnd,
  wishlistStoredItemPastRemoval,
} from '@utils/wishlistExpiry';

const STORAGE_KEY = 'pf_wishlist_v1';

const WishlistContext = createContext(null);

function readStoredItems() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const data = JSON.parse(raw);
    if (Array.isArray(data)) {
      return data.map((entry) =>
        typeof entry === 'string' ? { id: entry, endAt: null } : entry
      );
    }
    if (data?.items && Array.isArray(data.items)) return data.items;
  } catch {
    /* ignore */
  }
  return [];
}

function writeStoredItems(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: 1, items }));
  } catch {
    /* ignore */
  }
}

function pruneByStoredEndAt(items, now = new Date()) {
  return items.filter((x) => !wishlistStoredItemPastRemoval(x.endAt, now));
}

export const WishlistProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState(() => {
    const loaded = readStoredItems();
    return pruneByStoredEndAt(loaded);
  });

  useEffect(() => {
    writeStoredItems(items);
  }, [items]);

  useEffect(() => {
    const id = setInterval(() => {
      setItems((prev) => pruneByStoredEndAt(prev));
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const syncFromServer = async () => {
      if (!user?._id) return;

      try {
        const localPruned = pruneByStoredEndAt(readStoredItems());
        const { data } = await api.get('/auth/me/wishlist');
        const serverItems = pruneByStoredEndAt(Array.isArray(data?.items) ? data.items : []);

        const mergedById = new Map(serverItems.map((x) => [x.id, x]));
        for (const item of localPruned) {
          if (!mergedById.has(item.id)) {
            mergedById.set(item.id, item);
          }
        }
        const merged = Array.from(mergedById.values());

        if (cancelled) return;
        setItems(merged);

        if (merged.length !== serverItems.length || localPruned.length > 0) {
          await api.put('/auth/me/wishlist', { items: merged });
        }
      } catch {
        if (cancelled) return;
        setItems(pruneByStoredEndAt(readStoredItems()));
      }
    };

    syncFromServer();
    return () => {
      cancelled = true;
    };
  }, [user?._id]);

  const idsOrdered = useMemo(() => items.map((x) => x.id), [items]);

  const isWishlisted = useCallback(
    (activityId) => items.some((x) => x.id === activityId),
    [items]
  );

  const removeById = useCallback(
    (activityId) => {
      setItems((prev) => prev.filter((x) => x.id !== activityId));
      if (user?._id) {
        api.delete(`/auth/me/wishlist/${activityId}`).catch(() => {
          /* ignore network errors; UI remains responsive */
        });
      }
    },
    [user?._id]
  );

  const toggleWishlist = useCallback(
    (activity) => {
      const id = activity._id;
      const endAt = getWishlistActivityEnd(activity).toISOString();
      const category = activity.category || '';
      let exists = false;

      setItems((prev) => {
        exists = prev.some((x) => x.id === id);
        if (exists) return prev.filter((x) => x.id !== id);
        const next = [{ id, endAt }, ...prev.filter((x) => x.id !== id)];
        return pruneByStoredEndAt(next);
      });

      if (exists) {
        trackWishlistRemove({ activityId: id, category });
      } else {
        trackWishlistAdd({ activityId: id, category });
      }

      if (!user?._id) return;

      const request = exists
        ? api.delete(`/auth/me/wishlist/${id}`)
        : api.post(`/auth/me/wishlist/${id}`, { endAt });

      request.catch(() => {
        /* ignore network errors; full list will resync on next load */
      });
    },
    [user?._id]
  );

  /**
   * @param {object[]} foundActivities — successfully loaded activities
   * @param {string[]} notFoundIds — 404 ids to remove
   */
  const applyFetchResults = useCallback((foundActivities, notFoundIds = []) => {
    const notFound = new Set(notFoundIds);
    const byId = new Map(foundActivities.map((a) => [a._id, a]));
    setItems((prev) => {
      const next = [];
      for (const entry of prev) {
        if (notFound.has(entry.id)) continue;
        const act = byId.get(entry.id);
        if (!act) {
          next.push(entry);
          continue;
        }
        const endAt = getWishlistActivityEnd(act).toISOString();
        if (wishlistStoredItemPastRemoval(endAt)) continue;
        next.push({ id: entry.id, endAt });
      }
      return pruneByStoredEndAt(next);
    });
    if (user?._id && notFoundIds.length > 0) {
      Promise.all(notFoundIds.map((id) => api.delete(`/auth/me/wishlist/${id}`).catch(() => null))).catch(
        () => null
      );
    }
  }, [user?._id]);

  const value = useMemo(
    () => ({
      items,
      idsOrdered,
      isWishlisted,
      toggleWishlist,
      removeById,
      applyFetchResults,
    }),
    [items, idsOrdered, isWishlisted, toggleWishlist, removeById, applyFetchResults]
  );

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used within WishlistProvider');
  return ctx;
};
